import {Chip} from "@mui/material";
import {CellContent} from "../../components/ExTable";
import {TerminationMailType, TerminationStatus} from "../../app/api/model/termination";
import {TranslationContext, getTranslation} from "../../utils/getTranslationKey";
import {TFunction} from "i18next";
import MailIcon from "@mui/icons-material/Mail";
import MailRemindIcon from "@mui/icons-material/MarkEmailUnread";
import MailDoneIcon from "@mui/icons-material/MarkEmailRead";
import MailWithdrawnIcon from "@mui/icons-material/Unsubscribe";
import OneIcon from "@mui/icons-material/LooksOne";
import TwoIcon from "@mui/icons-material/LooksTwo";
import {formatTimestamp} from "../../utils/datetime";

export interface GetTerminationStatusLabel {
    status: TerminationStatus;
    t: TFunction;
}

export function getTerminationStatusCellContent({status, t}: GetTerminationStatusLabel): CellContent {
    const translatedStatus = getTranslation({context: TranslationContext.TERMINATION_STATUS, value: status, t});
    return {
        title: translatedStatus,
        node: getTerminationStatusChip({status, t}),
    };
}

export function getTerminationStatusChip({status, t}: GetTerminationStatusLabel): React.ReactNode {
    const translatedStatus = getTranslation({context: TranslationContext.TERMINATION_STATUS, value: status, t});
    let color: "info" | "success" | "warning" | undefined;
    switch (status) {
        case TerminationStatus.IN_PROGRESS:
            color = "info";
            break;
        case TerminationStatus.DONE:
            color = "success";
            break;
        case TerminationStatus.WITHDRAWN:
            color = "warning";
            break;
    }
    return <Chip label={translatedStatus} color={color} size="small" variant="filled" />;
}

export interface GetMailPendingIcon {
    mailType: TerminationMailType;
    mailAt?: string;
    t: TFunction;
}

export function getMailCellContent({mailType, mailAt, t}: GetMailPendingIcon): CellContent | undefined {
    if (!mailType) {
        return undefined;
    }
    const translatedMailType = getTranslation({context: TranslationContext.TERMINATION_MAIL_TYPE, value: mailType, t});

    return {
        title: `${translatedMailType}${mailAt ? " @ " + formatTimestamp(mailAt) : ""}`,
        node: getMailIcon(mailType),
    };
}

export function getMailIcon(mailType: TerminationMailType) {
    let icon: React.ReactNode = <MailRemindIcon color="warning" />;
    let subIcon: React.ReactNode;
    switch (mailType) {
        case TerminationMailType.INIT:
            icon = <MailIcon color="info" />;
            break;
        case TerminationMailType.REMINDER_1:
            subIcon = <OneIcon color="warning" />;
            break;
        case TerminationMailType.REMINDER_2:
            subIcon = <TwoIcon color="warning" />;
            break;
        case TerminationMailType.CLOSURE_MISSING:
            icon = <MailDoneIcon color="warning" />;
            break;
        case TerminationMailType.CLOSURE_COMPLETE:
            icon = <MailDoneIcon color="success" />;
            break;
        case TerminationMailType.CLOSURE_WITHDRAWN:
            icon = <MailWithdrawnIcon color="success" />;
            break;
    }
    return (
        <>
            {icon}
            {subIcon}
        </>
    );
}
